import { Input, Typography } from '@breetometer/components';
import { InfoIcon } from 'BreetAssets';
import { Tooltip, TooltipContent } from 'BreetComponents';
import { kybAuthAction, openModal, useAppDispatch } from 'BreetRedux';
import { type ChangeEvent, forwardRef, useCallback, useRef } from 'react';

import { getAcceptedFileTypes, getPreviewFileMeta, type IFileInput } from './FileInput.helpers';
import FileSelect from './select';

const FileInput = forwardRef<HTMLInputElement, IFileInput>(
	({ label, errorMsg, accept, name, value, onChange, id, tooltip }, ref) => {
		const fileInputRef = useRef<HTMLInputElement | null>(null);

		const { setPreviewFile } = kybAuthAction;

		const dispatch = useAppDispatch();

		const mainRef = ref ?? fileInputRef;

		const triggerFileSelect = useCallback(() => {
			if (typeof mainRef === 'function') return;
			mainRef.current?.click();
		}, [mainRef]);

		const { mime, acceptedMimeTypes } = getAcceptedFileTypes(accept);

		const prepareFilePreview = useCallback(
			(valueArg: string | FileList | undefined) => {
				if (valueArg) {
					const previewFile = getPreviewFileMeta(valueArg, id);
					dispatch(setPreviewFile(previewFile));
				}
			},
			[dispatch, id, setPreviewFile]
		);

		const handleFileChange = useCallback(
			(e: ChangeEvent<HTMLInputElement>) => {
				const file = e.target.files ?? null;
				if (!file?.length) return;
				onChange(file);
				prepareFilePreview(file);
			},
			[prepareFilePreview, onChange]
		);

		const onPreview = useCallback(() => {
			if (value) {
				prepareFilePreview(value);
				dispatch(
					openModal({
						id: 'previewKYBFile',
					})
				);
			}
		}, [dispatch, prepareFilePreview, value]);

		return (
			<div className='breet_kyb_form_file_input'>
				<div className='label-section'>
					<label htmlFor={id}>{label}</label>
					{tooltip && (
						<Tooltip tipElement={<TooltipContent content={tooltip} />}>
							<InfoIcon className='info-icon svg-dark' />
						</Tooltip>
					)}
				</div>

				<Typography
					as={value ? 'div' : 'button'}
					className='breet_kyb_form_file_input_wrapper bg-primary-600 border-dark-navy'
					onClick={value ? undefined : triggerFileSelect}
				>
					<div className='breet_kyb_form_file_input_wrapper_input'>
						<Input
							type='file'
							label=''
							id={id}
							accept={mime}
							ref={mainRef}
							name={name}
							onChange={handleFileChange}
						/>
					</div>

					<FileSelect
						acceptedMimeTypes={acceptedMimeTypes}
						value={value}
						onPreview={onPreview}
						onReplace={triggerFileSelect}
					/>
				</Typography>

				{!!errorMsg && (
					<div className='breet_kyb_form_file_input_error_message'>
						<Typography
							as='p'
							variant='sub_head'
							className='bt_input-error-message'
						>
							{errorMsg}
						</Typography>
					</div>
				)}
			</div>
		);
	}
);

export default FileInput;

FileInput.displayName = 'BreetFileInput';
